// janus-engine/ability/random-table-payload.ts — Janus lane A3 RT-4 (2026-10-01): the browser twin of
// apply_contingent_or_random_table_ability() (includes/functions/janus/core/apply_contingent_or_random_table_ability.php — deleted with
// this port) and of its two call sites: janus.php's dispatch block for an UNCONTESTED ability and the deferred block at the end of
// damage_roll() for a CONTESTED one (the targets that FAILED their contest). Owner ruling 2026-09-30 (late): random tables resolve in the
// browser — no backend bottleneck; the commit records what the page decided (rows + the range snapshot), never re-looking a range up.
//
// The decision is N2's pure router (../rolls/random-table-outcome.ts) over N2's table roller (../rolls/random-table.ts); this file is the
// engine half: it rolls on ctx.roll, writes the log rows and applies the outcome with applyStatusEffect.
//
// FAIRNESS (pre-draw): everything that could hand the use back is decided on STATE before the table's die is drawn —
//   · every table the roll can reach is held with its compiled bucket, rollable (no roll_type layer, no cycle, within the chain / face caps,
//     dice inside the commit's bounds);
//   · every outcome the table can hand out (or the contingent one) whose route needs its definition is held — the page hydrates them
//     (websocket/ability-truth.ts), and a missing one is a `preflight:` hand-back the adapter fetches and retries;
//   · on the dry runs (ctx.dryRun) every one of those outcomes is APPLIED once on isolated write views, so an outcome-dependent
//     applyStatusEffect hand-back (an immunity, a stage family, a gain trigger that would draw) is decided before the real draw too.

import { type JanusCtx, type Row, ecId, handBack, insertRoll, itemField, upper } from './context.js';
import { movementDecisionView } from '../movement/decision-view.js';
import { type JanusInput } from './janus.js';
import { applyStatusEffect } from './statuses/apply-status-effect.js';
import { MAX_TABLE_CHAIN, RANDOM_TABLE_KINDS, randomTableMaxEnd, readRandomTableDef, rollRandomTable, type RandomTableDef,
  type RandomTableRollResult } from '../rolls/random-table.js';
import { faceCount, MAX_FACES } from '../rolls/dice-roll.js';
import { randomTablePayloadOf, randomTableRowsForAbility, resolveRandomTableOutcome, RANDOM_TABLE_OUTCOME_ABILITY_TYPES,
  RANDOM_TABLE_OUTCOME_STATUS_TYPES, type RandomTableOutcomeDecision } from '../rolls/random-table-outcome.js';

const CONTESTING_ROLL = 'AD80D31D-898B-66CE-0894-1335D11B1529';
const DAMAGE_OVER_TIME_ROLL = '77A044B7-7041-CA96-3C63-BB718B15A364';
/** Counterspell and armor-bending ties share this table, independently of an authored payload layer. */
export const WILD_MAGIC_SURGE_TABLE_ID = '216D44B5-5C1D-E3DB-1463-4D8C8C59A796';
/** ability_truth_commit_inc.php's row bounds (dice ≤ 100, die ≤ 1000): a table whose pools exceed them could never commit. */
const COMMIT_MAX_DICE = 100, COMMIT_MAX_DIE = 1000, COMMIT_MAX_MOD = 10000, COMMIT_MAX_RESULT = 1000000, COMMIT_MAX_EFFECT = 750, COMMIT_MAX_DESCRIPTION = 20000;
/** PHP strlen() of the posted text (UTF-8 bytes) — the engine's tsconfig has no DOM lib (no TextEncoder). */
const utf8Bytes = (text: string | undefined): number => {
  let n = 0;
  for (const ch of text ?? '') { const c = ch.codePointAt(0)!; n += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4; }
  return n;
};
/** How many tables one pre-draw walk may visit (a wide nest of nested tables) before it hands back instead. */
const MAX_TABLE_VISITS = 256;

type Payload = NonNullable<ReturnType<typeof randomTablePayloadOf>>;

/** ft_ability_resolves_as_contest(): a contesting_roll, or a damage_over_time_roll carrying a skill_roll_contestee. */
export function resolvesAsContest(ctx: JanusCtx, ability: Row): boolean {
  const rollType = upper(ctx.read.value(ability, 'roll_type'));
  return rollType === CONTESTING_ROLL || (rollType === DAMAGE_OVER_TIME_ROLL && ctx.read.has(ability, 'skill_roll_contestee'));
}
/** The ability carries a `contingent_ability` / `roll_random_table` payload (a UUID one — PHP's check_uuid gate). */
export function hasRandomTablePayload(ability: Row): boolean { return randomTablePayloadOf(ability) !== null; }

const tableDef = (ctx: JanusCtx, id: string): RandomTableDef | null => readRandomTableDef(id, ctx.read.definition(upper(id)));

/** Every table the roll can reach is held and rollable, and the worst chain stays inside the roller's caps — on state, pre-draw. */
export function randomTableChainPreDraw(ctx: JanusCtx, tableId: string): void {
  let visits = 0;
  const visit = (id: string, depth: number, path: readonly string[]): number => {
    if (++visits > MAX_TABLE_VISITS) handBack('random_table_roll:chain-too-wide');
    if (depth >= MAX_TABLE_CHAIN) handBack('random_table_roll:chain-too-long');
    const def = tableDef(ctx, id);
    if (!def) handBack(`random_table_roll:table-not-held:${upper(id)}`);
    if (def.rollType !== null) handBack('random_table_roll:unsupported:roll_type');
    const pools = def.authored ? def.pools : [{ dice: 1, die: randomTableMaxEnd(def.ranges), mod: 0 }];
    if (!pools.length || pools.some(p => p.die <= 0)) {
      // the top table with no rows refuses; a nested one ends the chain silently (handle_random_table_item_id_attached)
      if (depth === 0) handBack('random_table_roll:empty-table');
      return 0;
    }
    // the ROW the commit stores sums the pools: dice ≤ 100, die ≤ 1000, mod within ±10 000, the result within ±1 000 000 (Codex RT-4 review)
    const sumDice = pools.reduce((s, p) => s + p.dice, 0), sumMod = pools.reduce((s, p) => s + p.mod, 0), maxResult = pools.reduce((s, p) => s + p.dice * p.die, 0) + sumMod;
    if (sumDice > COMMIT_MAX_DICE || pools.some(p => p.die > COMMIT_MAX_DIE) || Math.abs(sumMod) > COMMIT_MAX_MOD
        || Math.abs(maxResult) > COMMIT_MAX_RESULT || Math.abs(sumDice + sumMod) > COMMIT_MAX_RESULT) handBack('random_table_roll:dice-out-of-commit-range');
    // the commit stores a range's snapshot text only inside its bounds (effect ≤ 750 B, description ≤ 20 000 B, after the authoring purifier,
    // which is idempotent on authored text): a range that could not commit is decided here, before the draw (Codex RT-4 review)
    if (def.ranges.some(r => utf8Bytes(r.effect) > COMMIT_MAX_EFFECT || utf8Bytes(r.description) > COMMIT_MAX_DESCRIPTION))
      handBack('random_table_roll:range-text-out-of-commit-range');
    const own = pools.reduce((sum, p) => sum + faceCount(p.dice, p.die, 0), 0);
    let worst = 0;
    for (const range of def.ranges) {
      const attached = range.attached;
      if (!attached) continue;
      const type = range.attached_type ?? '';
      if (RANDOM_TABLE_KINDS.has(type)) {
        // below the top, a range attaching the table it came from ends the chain; the TOP re-rolls itself once ("roll again")
        if (depth > 0 && attached === def.id) continue;
        if (path.includes(attached) && !(depth === 0 && attached === def.id)) handBack('random_table_roll:chain-cycle');
        worst = Math.max(worst, visit(attached, depth + 1, [...path, def.id]));
      } else if (type === 'creature' && (range.qty_dice ?? 0) > 0 && (range.qty_die ?? 0) > 0) {
        if (depth + 1 >= MAX_TABLE_CHAIN) handBack('random_table_roll:chain-too-long');
        if (range.qty_dice! > COMMIT_MAX_DICE || range.qty_die! > COMMIT_MAX_DIE || Math.abs(range.qty_mod ?? 0) > COMMIT_MAX_MOD
            || Math.abs(range.qty_dice! * range.qty_die! + (range.qty_mod ?? 0)) > COMMIT_MAX_RESULT) handBack('random_table_roll:dice-out-of-commit-range');
        worst = Math.max(worst, faceCount(range.qty_dice!, range.qty_die!, 0));
      }
    }
    return own + worst;
  };
  if (visit(tableId, 0, []) > MAX_FACES) handBack('random_table_roll:too-many-faces');
}

/** The outcomes the payload can hand out: the contingent id, or each distinct TOP-range attachment (with the bucket's type hint). */
function candidateOutcomes(ctx: JanusCtx, payload: Payload): Array<{ id: string; hint: string | null }> {
  if (payload.kind === 'contingent') return [{ id: payload.outcomeId, hint: null }];
  const seen = new Map<string, string | null>();
  for (const range of tableDef(ctx, payload.tableId)?.ranges ?? []) if (range.attached && !seen.has(range.attached)) seen.set(range.attached, range.attached_type ?? null);
  return [...seen].map(([id, hint]) => ({ id, hint }));
}

/** The router's decision for one outcome, against the creature targets (landed = null: the uncontested shape; the probe's superset). */
function decide(ctx: JanusCtx, parent: Row, payload: Payload, outcome: { id: string; hint: string | null }, caster: Row, targets: Row[],
  landed: Row[] | null): RandomTableOutcomeDecision {
  const rolled: RandomTableRollResult | null = payload.kind === 'table'
    ? { steps: [], range: null, attached: outcome.id, attachedType: outcome.hint } : null;
  return resolveRandomTableOutcome({ parent, rolled, outcomeDef: ctx.read.definition(outcome.id), outcomeTypeHint: outcome.hint,
    casterId: ecId(caster), targetIds: targets.map(ecId), landedIds: landed ? landed.map(ecId) : null });
}

/** A table attachment with no type hint is a DELETED item (the bucket's LEFT JOIN drops it): legacy's query_full_item_arr() found
 *  nothing and applied nothing — not a definition the page must fetch. */
const deletedAttachment = (payload: Payload, outcome: { hint: string | null }, def: Row | null): boolean =>
  payload.kind === 'table' && !outcome.hint && !def;

/** Apply one routed decision. `members` resolves an id to the row the write lands on (the ctx's own, or a probe's clones). */
function applyDecision(ctx: JanusCtx, input: JanusInput, parent: Row, caster: Row, decision: RandomTableOutcomeDecision,
  member: (id: string) => Row | undefined): void {
  if (decision.kind === 'status') {
    const outcomeDef = ctx.read.definition(decision.outcomeId);
    if (!outcomeDef) handBack('apply_contingent_or_random_table_ability:outcome-not-held');
    // the DURATION source: a status_effect governs its own, a passive takes the parent's ("duration set by the preceding ability")
    const source = decision.durationSource === 'parent' ? parent : outcomeDef;
    for (const id of decision.targetIds) {
      const target = member(id);
      if (!target) handBack('apply_contingent_or_random_table_ability:target-not-held');
      // a FRESH connection per recipient (PHP: status_connected_id new_guid() each) — never the cast's shared one (Codex RT-4 review)
      applyStatusEffect(ctx, input, source, decision.outcomeId, decision.logRollType, caster, target, undefined, true);
    }
  } else if (decision.kind === 'grant') {
    // ability-bucket outcome → granted to the caster as a status (status_item_id = outcome, origin = parent), TTL from the parent
    if (!ctx.read.definition(decision.outcomeId)) handBack('apply_contingent_or_random_table_ability:outcome-not-held');
    applyStatusEffect(ctx, input, parent, decision.outcomeId, decision.logRollType, caster, caster, undefined, true);
  }
}

/** Dry runs only: apply every candidate outcome once, each on its own view of the state it writes, so an outcome-dependent hand-back is
 *  pre-draw. Run on EVERY dry run at the state that run reached (no memo — Codex RT-4 review: a contest's landed set and the rows it
 *  changed differ per synthetic run). The shared decision view borrows untouched member branches and queue prefixes;
 *  writes to a member, a queued change or the encounter belong only to this candidate. */
function probeOutcomes(ctx: JanusCtx, input: JanusInput, parent: Row, payload: Payload, caster: Row, targets: Row[]): void {
  const refuseDraw = (): never => handBack('apply_contingent_or_random_table_ability:outcome-draws');
  for (const outcome of candidateOutcomes(ctx, payload)) {
    const decision = decide(ctx, parent, payload, outcome, caster, targets, null);
    if (decision.kind !== 'status' && decision.kind !== 'grant') continue;
    const { encounter: state, members } = movementDecisionView(ctx as unknown as Row, ctx.members, { borrowMembers: true });
    const probe = state as unknown as JanusCtx;
    probe.members = members;
    probe.read = ctx.read;
    probe.rows = [];
    probe.roll = { d: refuseDraw, fraction: refuseDraw };
    probe.priorDispatchStatuses = undefined;
    applyDecision(probe, input, parent, members.get(ecId(caster))!, decision, id => members.get(upper(id)));
  }
}

/** Every pre-draw check (state only): the tables, the outcome definitions, and — on a dry run — every outcome applied once. */
function payloadPreDraw(ctx: JanusCtx, input: JanusInput, parent: Row, payload: Payload, caster: Row, targets: Row[]): void {
  if (payload.kind === 'table') randomTableChainPreDraw(ctx, payload.tableId);
  for (const outcome of candidateOutcomes(ctx, payload)) {
    const def = ctx.read.definition(outcome.id);
    if (def || deletedAttachment(payload, outcome, def)) continue;
    const hint = outcome.hint ?? '';
    // a nested table / creature / loot outcome is log-only; a status / passive / ability outcome needs its definition to apply
    if (payload.kind === 'contingent' || RANDOM_TABLE_OUTCOME_STATUS_TYPES.has(hint) || RANDOM_TABLE_OUTCOME_ABILITY_TYPES.has(hint))
      handBack(`apply_contingent_or_random_table_ability:outcome-not-held:${outcome.id}`);
  }
  if (ctx.dryRun) probeOutcomes(ctx, input, parent, payload, caster, targets);
}

/** DRY RUN ONLY: the table "rolls" the first range carrying `attached` (null = a range attaching nothing) — one synthetic top row in the
 *  table's own dice shape (the faces are the dry run's, never committed), no nested chain (those rows only log; pre-draw walked them). */
function forcedTableRoll(ctx: JanusCtx, tableId: string, attached: string | null): ReturnType<typeof rollRandomTable> {
  const def = tableDef(ctx, tableId);
  if (!def) return { ok: false, need: tableId };
  const range = def.ranges.find(r => (r.attached ?? null) === attached) ?? null;
  const pools = def.authored ? def.pools : [{ dice: 1, die: randomTableMaxEnd(def.ranges), mod: 0 }];
  const value = range ? range.begin : 1;
  const dice = pools.reduce((s, p) => s + p.dice, 0), die = new Set(pools.map(p => p.die)).size === 1 ? pools[0]!.die : 0;
  return { ok: true, result: { range, attached: range?.attached ?? null, attachedType: range?.attached_type ?? null, steps: [{ roll_type_1: def.itemType,
    roll_type_2: '', roll_item_id: def.id, pools: pools.map(p => ({ ...p, faces: [] })), dice, die, roll_mod: pools.reduce((s, p) => s + p.mod, 0),
    adv_dis_stacks: 0, roll_result: value, rolls_full: JSON.stringify([[Math.max(1, Math.min(value, die || value)), `d${die || value}_1`, { kept: true }]]),
    random_table: { effect: range?.effect ?? null, description: range?.description ?? null, attached_item_id: range?.attached ?? null } }] } };
}

/**
 * apply_contingent_or_random_table_ability(): roll the parent's table (or take its contingent outcome), log the roll rows, route the
 * outcome. `landed` = null for an UNCONTESTED ability (called at dispatch, before the templates — janus.php's block); for a CONTESTED one
 * the targets that FAILED their contest (called after the contest — damage_roll's deferred block): `[]` = everyone resisted → a
 * target-directed outcome lands on NO ONE (never the caster), while a caster grant still lands.
 */
export function applyRandomTablePayload(ctx: JanusCtx, input: JanusInput, parent: Row, caster: Row, targets: Row[], landed: Row[] | null): void {
  const payload = randomTablePayloadOf(parent);
  if (!payload) return;
  const creatures = targets.filter(t => !!ecId(t));
  payloadPreDraw(ctx, input, parent, payload, caster, creatures);
  let rolled: RandomTableRollResult | null = null;
  if (payload.kind === 'table') {
    const tableId = upper(payload.tableId);
    // DRY RUNS: record which outcomes this table can hand out, so the caller re-runs the WHOLE use once per outcome (randomTableForced);
    // the extreme dice alone reach only the table's end ranges (Codex RT-4 review: a middle outcome's later gates were post-draw)
    if (ctx.dryRun && ctx.randomTableSeen && !ctx.randomTableSeen.has(tableId))
      ctx.randomTableSeen.set(tableId, [...new Set((tableDef(ctx, tableId)?.ranges ?? []).map(r => r.attached ?? null))]);
    const forced = ctx.dryRun && ctx.randomTableForced?.tableId === tableId ? ctx.randomTableForced : null;
    const out = forced ? forcedTableRoll(ctx, tableId, forced.attached) : rollRandomTable(payload.tableId, id => tableDef(ctx, id), die => ctx.roll.d(die));
    // unreachable after the pre-draw walk (it proves every reachable table held and rollable) — kept as a loud guard
    if (!out.ok) handBack(`random_table_roll:${'need' in out ? 'table-not-held' : out.refuse}`);
    rolled = out.result;
    // random_table_roll(): every chained row is logged by the caster to the primary creature target, with its range snapshot
    for (const step of randomTableRowsForAbility(rolled, creatures.map(ecId))) {
      insertRoll(ctx, { roll_type_1: step.roll_type_1, roll_type_2: step.roll_type_2, roll_item_id: step.roll_item_id, by: caster,
        to: step.roll_to ? ctx.members.get(step.roll_to) ?? null : null, roll_result: step.roll_result,
        rolls_full: JSON.parse(step.rolls_full) as unknown[], dice: step.dice, die: step.die, mod: step.roll_mod,
        adv_dis_stacks: step.adv_dis_stacks, random_table: step.random_table });
    }
  }
  const outcomeId = payload.kind === 'contingent' ? payload.outcomeId : rolled?.attached ?? null;
  if (!outcomeId) return;
  const outcome = { id: outcomeId, hint: payload.kind === 'table' ? rolled?.attachedType ?? null : null };
  const outcomeDef = ctx.read.definition(outcomeId);
  if (deletedAttachment(payload, outcome, outcomeDef)) return;
  // DRY RUNS of a CONTESTED payload land on EVERY creature target (the superset): a synthetic run's contest may resist where the real dice
  // land, and the outcome's commit gate must be met before the draw either way (Codex RT-4 re-review P1)
  const decision = decide(ctx, parent, payload, outcome, caster, creatures, ctx.dryRun && landed !== null ? creatures : landed);
  // pre-draw proved every routable outcome held; a status / grant reaching here unheld is a guard, not a path
  if (decision.kind === 'none' && decision.reason === 'outcome-not-held') handBack('apply_contingent_or_random_table_ability:outcome-not-held');
  applyDecision(ctx, input, parent, caster, decision, id => ctx.members.get(upper(id)));
}

/** The ids the page should hold before a use whose ability (or reaction) carries a payload: the tables the roll can reach and every
 *  outcome whose route needs a definition. `def(id)` reads what the page holds; unheld nested tables are returned for the next pass. */
export function randomTablePayloadHydrationIds(ability: Row, def: (id: string) => Row | null): { tables: string[]; outcomes: string[] } {
  const payload = randomTablePayloadOf(ability);
  if (!payload) return { tables: [], outcomes: [] };
  if (payload.kind === 'contingent') return { tables: [], outcomes: [payload.outcomeId] };
  const tables: string[] = [], outcomes = new Set<string>();
  const seen = new Set<string>();
  const queue = [payload.tableId];
  while (queue.length && seen.size < MAX_TABLE_VISITS) {
    const id = upper(queue.shift());
    if (seen.has(id)) continue;
    seen.add(id);
    const held = readRandomTableDef(id, def(id));
    if (!held) { tables.push(id); continue; }
    for (const range of held.ranges) {
      if (!range.attached) continue;
      const type = range.attached_type ?? '';
      if (RANDOM_TABLE_KINDS.has(type)) queue.push(range.attached);
      // only the TOP table's outcome is applied (nested tables only log)
      else if (id === upper(payload.tableId) && (RANDOM_TABLE_OUTCOME_STATUS_TYPES.has(type) || RANDOM_TABLE_OUTCOME_ABILITY_TYPES.has(type)))
        outcomes.add(range.attached);
    }
  }
  return { tables, outcomes: [...outcomes] };
}

/** Requested table batches include their saved ranges and result definitions.
 * Missing reads stay unheld for the named pre-draw refusal. Walk newly held nests before drawing. */
export async function hydrateRandomTablePayloadDefinitions(carriers: readonly Row[], extraTableIds: readonly string[],
  def: (id: string) => Row | null, fetchDefinitions: (ids: string[]) => Promise<unknown>): Promise<void> {
  const tables = new Set(extraTableIds.map(upper));
  if (carriers.some(carrier => itemField(carrier, 'item_type') === 'spell')) tables.add(WILD_MAGIC_SURGE_TABLE_ID);
  const all = [...carriers, ...[...tables].map(id => ({ item_layer_types: { roll_random_table: id } }))];
  const attempted = new Set<string>();
  for (let pass = 0; pass < MAX_TABLE_CHAIN; pass++) {
    const wantedTables = new Set<string>(), wantedOutcomes = new Set<string>();
    for (const carrier of all) {
      const ids = randomTablePayloadHydrationIds(carrier, def);
      for (const id of ids.tables) if (!attempted.has(id)) wantedTables.add(id);
      for (const id of ids.outcomes) if (!def(id) && !attempted.has(id)) wantedOutcomes.add(id);
    }
    if (!wantedTables.size && !wantedOutcomes.size) return;
    for (const id of [...wantedTables, ...wantedOutcomes]) attempted.add(id);
    await fetchDefinitions([...new Set([...wantedTables, ...wantedOutcomes])]);
  }
}
